/* =============================================================================
   CORE / STORAGE-ERRORS — Supabase-Storage-Fehler normalisieren (S270 §B)
   =============================================================================
   Befund (S269-Retest): supabase-js 2.45.4 wirft beim `download()` eines
   gelöschten Objekts KEINEN StorageApiError mit `status`, sondern einen
   `StorageUnknownError { name, message: '{}', originalError: Response }` — die
   Response hat Status 400 und im Body
     {"statusCode":"404","error":"not_found","message":"Object not found","code":"NoSuchKey"}.
   Am Fehlerobjekt selbst steht nichts davon. Wer nur `status`/`message` liest,
   sieht „{}" — so kam „⚠ Bon-PDF konnte nicht geladen werden.{}" ins Archiv.

   `normalizeStorageError()` liest `originalError` (Status + Body) und liefert
   einen gewöhnlichen Error mit `code` ('not_found' | Body-code | Original),
   `status` (404 bei not_found) und lesbarer `message`. `isStorageNotFound()`
   erkennt den Fall auch an schon normalisierten oder rohen Objekten.

   Genutzt von: public/adapters/storage.js (bonArchive/schedulePdfs
   downloadPdfBlob, über window.GgStorageErrors), archive-mails.js (📎-Rückfall
   nach der Dienstplan-Aufbewahrung), orders-receipt-actions.js (Modal-Log).
============================================================================ */

const NOT_FOUND_RE = /object not found|not_found|nosuchkey|\bnot found\b/i;

function _num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Ist das ein „Objekt fehlt"-Fall? Prüft Status, Body-Marker und Text. */
function _notFoundShape(status, body, message) {
  if (status === 404) return true;
  if (body && typeof body === 'object') {
    if (_num(body.statusCode) === 404) return true;
    if (String(body.error || '').toLowerCase() === 'not_found') return true;
    if (String(body.code || '') === 'NoSuchKey') return true;
    if (NOT_FOUND_RE.test(String(body.message || ''))) return true;
  }
  return NOT_FOUND_RE.test(String(message || ''));
}

/** Body einer Response lesen, ohne den Stream zu zerstören; null bei allem anderen. */
async function _readBody(orig) {
  if (!orig || typeof orig !== 'object') return null;
  if (orig.body && typeof orig.body === 'object' && typeof orig.json !== 'function') return orig.body;
  if (typeof orig.json !== 'function') return null;
  try {
    const r = (typeof orig.clone === 'function' && !orig.bodyUsed) ? orig.clone() : orig;
    return await r.json();
  } catch (_) {
    return null;
  }
}

/**
 * Normalisiert einen Storage-Fehler (StorageUnknownError/StorageApiError/Error).
 * Liefert immer einen Error mit `code`, `status`, `body`, `originalError`.
 * Idempotent: ein bereits normalisierter Fehler kommt unverändert zurück.
 */
export async function normalizeStorageError(err) {
  if (!err || typeof err !== 'object') return new Error(String(err || 'Storage-Fehler'));
  if (err.__ggNormalized) return err;
  let status = _num(err.status ?? err.statusCode);
  let body = null;
  const orig = err.originalError;
  if (orig && typeof orig === 'object') {
    status = _num(orig.status ?? orig.statusCode) || status;
    body = await _readBody(orig);
  }
  const notFound = _notFoundShape(status, body, err.message);
  const bodyMsg = body && typeof body === 'object' ? String(body.message || '') : '';
  const rawMsg = String(err.message || '');
  const message = notFound
    ? 'Object not found'
    : (bodyMsg || (rawMsg && rawMsg !== '{}' ? rawMsg : '') || 'Storage-Fehler');
  const out = new Error(message);
  out.name = err.name || 'StorageError';
  out.code = notFound ? 'not_found' : ((body && (body.code || body.error)) || err.code || undefined);
  out.status = notFound ? 404 : (status || undefined);
  out.body = body;
  out.originalError = err;
  out.__ggNormalized = true;
  return out;
}

/** Synchron: ist dieser (rohe oder normalisierte) Fehler ein „Objekt fehlt"? */
export function isStorageNotFound(err) {
  if (!err) return false;
  if (typeof err === 'string') return NOT_FOUND_RE.test(err);
  if (err.code === 'not_found' || err.code === 'NoSuchKey') return true;
  const status = _num(err.status ?? err.statusCode);
  if (status === 404) return true;
  if (_notFoundShape(0, err.body, err.message)) return true;
  const orig = err.originalError;
  if (orig && typeof orig === 'object') {
    const os = _num(orig.status ?? orig.statusCode);
    if (os === 404) return true;
    // Status 400 mit not_found-Body ist die 2.45.4-Form; ohne gelesenen Body
    // bleibt nur der Statuscode — erst normalizeStorageError() macht ihn sicher.
    if (_notFoundShape(0, orig.body && typeof orig.body === 'object' && typeof orig.json !== 'function' ? orig.body : null, orig.statusText)) return true;
  }
  return false;
}

if (typeof window !== 'undefined') {
  window.GgStorageErrors = { normalizeStorageError, isStorageNotFound };
}
